import React, { useState } from 'react';

// Public Supabase bucket where class photos live (same bucket as treatment photos).
const BUCKET = 'https://mhqbsdexwcqmdchsviig.supabase.co/storage/v1/object/public/treatment-images';

// Photos may be uploaded with any of these extensions, and either a lowercase
// or Capitalised filename (Supabase keys are case-sensitive) — we try each combo.
const EXTS = ['png', 'jpg', 'jpeg'];
function candidateFiles(slug) {
  const cap = slug.charAt(0).toUpperCase() + slug.slice(1);
  const names = cap === slug ? [slug] : [slug, cap];
  const files = [];
  for (const n of names) {
    for (const e of EXTS) files.push(`${n}.${e}`);
  }
  return files;
}

// Known classes → type, used for slug matching and the coloured fallback tile.
const CLASS_TYPES = [
  ['Strong', 'Strength'], ['Vortex', 'Strength'], ['Armageddon', 'Strength'],
  ['Spin', 'Cardio'], ['Burn', 'Cardio'], ['Box', 'Cardio'], ['FIT', 'Cardio'], ['Fusion', 'Cardio'],
  ['Core', 'Mobility'], ['Balance', 'Mobility'], ['Stretch', 'Mobility'], ['Spine', 'Mobility'], ['Yoga', 'Mobility'],
];
const TYPE_COLOURS = { Strength: '#FF6200', Cardio: '#D32F2F', Mobility: '#1565C0' };

// Match longest class names first so e.g. "Spine" resolves before "Spin".
const SORTED = [...CLASS_TYPES].sort((a, b) => b[0].length - a[0].length);

// Explicit overrides for sessions whose photo file isn't named after the class
// itself: [name substring, photo slug, fallback colour]. Checked before the
// general class match below. e.g. an "Open Gym" booking uses the dumbells photo.
const SLUG_OVERRIDES = [
  ['open gym', 'dumbells', '#1A1A1A'],
  ['junior', 'juniorcoaching', '#1565C0'],
  ['kettlebell', 'kettlebells', '#FF6200'],
  ['flow', 'flow', '#1565C0'],
  ['inbody', 'inbody', '#37474F'],
];

// Normalise a name/key for matching: lowercase + strip non-alphanumerics, so
// "In Body Scan" → "inbodyscan", "Open Gym" → "opengym", "Stretch & Core" →
// "stretchcore". Makes matching insensitive to spaces, &, hyphens, etc.
const norm = s => (s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Resolve an arbitrary TeamUp class name to its photo slug + fallback colour.
export function classInfo(name) {
  const flat = norm(name);
  // Combined "Stretch & Core" session (any order/separator) shares one photo.
  if (flat.includes('stretch') && flat.includes('core')) {
    return { slug: 'stretchandcore', colour: '#1565C0' };
  }
  const override = SLUG_OVERRIDES.find(([key]) => flat.includes(norm(key)));
  if (override) return { slug: override[1], colour: override[2] };
  const match = SORTED.find(([n]) => flat.includes(norm(n)));
  if (!match) return { slug: null, colour: '#1A1A1A' };
  return { slug: match[0].toLowerCase(), colour: TYPE_COLOURS[match[1]] };
}

// Renders a class photo. Tries .png → .jpg → .jpeg, then falls back to a
// coloured tile with an emoji if no photo exists for the class yet.
export default function ClassPhoto({
  name,
  width = 52,
  height = 52,
  radius = 10,
  emoji = '💪',
  fontSize,
}) {
  const { slug, colour } = classInfo(name);
  const files = slug ? candidateFiles(slug) : [];
  const [idx, setIdx] = useState(0); // index into files; >= length means fall back to tile
  const baseStyle = { width, height, borderRadius: radius, flexShrink: 0, objectFit: 'cover', display: 'block' };

  if (idx < files.length) {
    return (
      <img
        src={`${BUCKET}/${files[idx]}`}
        alt={name || 'Class'}
        style={baseStyle}
        onError={() => setIdx(i => i + 1)}
      />
    );
  }

  const px = typeof height === 'number' ? height : 52;
  return (
    <div style={{
      ...baseStyle,
      background: colour,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      fontSize: fontSize || Math.round(px * 0.42),
    }}>
      {emoji}
    </div>
  );
}
